import { observer } from 'mobx-react-lite';
import type { Moment } from 'moment-timezone';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  MailrailBaseEmailCampaign,
  MailrailPartnerMessage as MailrailPartnerMessageModel,
} from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import {
  CardContent,
  CardHeader,
  CardV2,
  DatePicker,
  Fieldset,
  Form,
  Radios,
} from '@feathr/components';
import ConsentCapture from '@feathr/extender/components/ConsentCapture';
import { useUser } from '@feathr/extender/state';
import { flattenError, moment, TimeFormat } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

import MailrailCampaignSegments from '../../MailrailCampaignSegments';
import MailrailCampaignSendingOptions from '../../MailrailCampaignSendingOptions';
import PinpointEmailCampaignAudienceSummary from '../../PinpointEmailCampaignAudienceSummary';
import { copyRoundedTimeOfDay, getDefaultStartTimestamp } from '../dates.utils';
import MailrailPartnerAudienceSection from '../MailrailPartnerAudienceSection';

import * as styles from './MailrailCampaignStepScheduleSend.css';

interface IProps {
  campaign: MailrailBaseEmailCampaign;
  disabled: boolean;
  stepNumber: number;
}

interface IErrors extends TValidateGrouped {
  bulk_send_enabled?: string[];
  bulk_send_timing?: string[];
  'consent.has_consent'?: string[];
  date_end?: string[];
  date_enrollment_end?: string[];
  date_start?: string[];
  'participation.mode'?: string[];
  'participation.partner_ids'?: string[];
  repeat_allowed?: string[];
  repeat_cooldown_seconds?: string[];
  segments_excluded?: string[];
  segments_included?: string[];
}

export function validateStepScheduleSend(campaign: MailrailBaseEmailCampaign): IErrors {
  // Drips share the auto-send scheduling window (start/end dates + bulk send + repeat).
  const isTriggerable = campaign.isMailrailTriggerable;
  const validationFields = ['consent.has_consent', 'date_start'];
  if (campaign.isMailrailPartnerMessage) {
    validationFields.push('participation.mode', 'participation.partner_ids');
  } else {
    validationFields.push('segments_excluded', 'segments_included');
  }
  if (isTriggerable) {
    validationFields.push(
      'bulk_send_enabled',
      'bulk_send_timing',
      'date_end',
      'repeat_allowed',
      'repeat_cooldown_seconds',
    );
  }
  if (campaign.isMailrailDrip) {
    // Enrollment cutoff: optional, but must fall within (date_start, date_end) when set.
    validationFields.push('date_enrollment_end');
  }
  return campaign.validate(validationFields, false, 'grouped').errors;
}

function MailrailCampaignStepScheduleSend({
  campaign,
  disabled,
  stepNumber,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const user = useUser();
  const stats = campaign.get('stats_cache');

  const userTimezone = user.get('timezone');
  // Drips share the auto-send scheduling window (start/end + bulk + repeat) plus an enrollment cutoff.
  const isTriggerable = campaign.isMailrailTriggerable;
  const isTimeSubtype = isTriggerable && campaign.get('subtype') === 'time';
  const dateStart = campaign.get('date_start');
  const dateEnd = campaign.get('date_end');
  const sendSchedule = dateStart ? 'later' : 'now';
  const startMoment = moment.utc(dateStart);
  const startMomentLocalized = userTimezone
    ? startMoment.clone().tz(userTimezone)
    : startMoment.clone().local();
  const endMoment = dateEnd ? moment.utc(dateEnd) : undefined;
  const endMomentLocalized = userTimezone
    ? endMoment?.clone().tz(userTimezone)
    : endMoment?.clone().local();
  const dateEnrollmentEnd = campaign.get('date_enrollment_end');
  const enrollmentEndMoment = dateEnrollmentEnd ? moment.utc(dateEnrollmentEnd) : undefined;
  const enrollmentEndMomentLocalized = userTimezone
    ? enrollmentEndMoment?.clone().tz(userTimezone)
    : enrollmentEndMoment?.clone().local();

  function onSendtimeChange(newValue?: string): void {
    if (newValue === 'now') {
      campaign.set({ date_start: undefined });
    }

    if (newValue === 'later') {
      campaign.set({ date_start: getDefaultStartTimestamp() });
    }
  }

  // For the time subtype, a date picker should only change the date component of its field; the
  // time component stays synced to the campaign's send time-of-day (from timeSource).
  function toScheduleTimestamp(
    newTimestamp: string | undefined,
    timeSource: Moment,
  ): string | undefined {
    if (!isTimeSubtype) {
      return newTimestamp;
    }

    const newDate = userTimezone
      ? moment.tz(newTimestamp, userTimezone)
      : moment(newTimestamp).local();

    copyRoundedTimeOfDay(newDate, timeSource);
    return newDate.utc().format(TimeFormat.isoDateTime);
  }

  function onStartDateChange(newTimestamp: string | undefined): void {
    campaign.set({ date_start: toScheduleTimestamp(newTimestamp, startMomentLocalized) });
  }

  function onEndDateChange(newTimestamp: string | undefined): void {
    const timeSource = endMomentLocalized ?? startMomentLocalized;
    campaign.set({ date_end: toScheduleTimestamp(newTimestamp, timeSource) });
  }

  function onEnrollmentEndChange(newTimestamp?: string): void {
    const timeSource = endMomentLocalized ?? startMomentLocalized;
    campaign.set({ date_enrollment_end: toScheduleTimestamp(newTimestamp, timeSource) });
  }

  function onClearEnrollmentEnd(): void {
    campaign.set({ date_enrollment_end: undefined });
  }

  function handleChangeTimeOfDay(newTimestamp?: string): void {
    // Update the time component of date_end without changing its date component
    const newTime = moment.utc(newTimestamp);
    const end = moment.utc(dateEnd);
    copyRoundedTimeOfDay(end, newTime);
    const formattedEnd = end.format(TimeFormat.isoDateTime);
    campaign.set({ date_end: formattedEnd });
    // Start time is only updated if the campaign hasn't begun yet.
    // Once the campaign is running, daily send time is controlled by date_end,
    // and date_start is frozen for reporting accuracy.
    if (campaign.isDraft || campaign.isScheduled) {
      const start = moment.utc(dateStart);
      copyRoundedTimeOfDay(start, newTime);
      const formattedStart = start.format(TimeFormat.isoDateTime);
      campaign.set({ date_start: formattedStart });
    }
  }

  function handleChangeConsent(newValue = false): void {
    if (newValue) {
      campaign.set({
        consent: {
          user: user!.id,
          has_consent: true,
          date_consented: moment.utc().format(TimeFormat.isoDateTime),
        },
      });
    } else {
      campaign.set({
        consent: {
          has_consent: false,
        },
      });
    }
  }

  function getMinStartDate(): Moment {
    const nowLocal = userTimezone ? moment.tz(userTimezone) : moment().local();
    const minStart = nowLocal.clone().add(15, 'minutes');
    // When a time subtype autosend's send time is earlier than now + 15min, the minDate of the picker
    // should be the following day because the campaign can't actually send anything until then
    if (isTimeSubtype) {
      const sendTime = copyRoundedTimeOfDay(minStart.clone(), startMomentLocalized);
      if (sendTime.isBefore(minStart)) {
        return minStart.clone().add(1, 'day');
      }
    }
    return minStart;
  }

  function getMinEndDate(): Moment {
    const nowLocal = userTimezone ? moment.tz(userTimezone) : moment().local();

    if (campaign.hasStarted && isTimeSubtype && endMomentLocalized) {
      const nextSendTime = copyRoundedTimeOfDay(nowLocal.clone(), endMomentLocalized);
      if (nextSendTime.isBefore(nowLocal)) {
        nextSendTime.add(1, 'day');
      }
      return moment.max([getMinStartDate().clone().add(1, 'day'), nextSendTime]);
    }

    if (campaign.hasStarted || sendSchedule === 'now') {
      const nowPlusFifteenMinutes = nowLocal.clone().add(15, 'minutes');
      const startPlusOneDay = startMomentLocalized.clone().add(1, 'day');
      return moment.max([nowPlusFifteenMinutes, startPlusOneDay]);
    }

    return moment.max([getMinStartDate(), startMomentLocalized]).clone().add(1, 'day');
  }

  function getMaxEnrollmentCutoff(): Moment | undefined {
    if (!endMomentLocalized) {
      return undefined;
    }
    return endMomentLocalized.clone().subtract(1, 'day');
  }

  function getMinTime(dateSelection: Moment, minDate: Moment): Moment {
    const nowLocal = userTimezone ? moment().tz(userTimezone) : moment().local();
    if (!dateSelection) {
      return minDate;
    }
    if (dateSelection && minDate.isAfter(dateSelection.clone().endOf('day'))) {
      return nowLocal.endOf('day');
    }
    if (dateSelection && minDate.isAfter(dateSelection.clone().startOf('day'))) {
      return minDate;
    }
    return nowLocal.startOf('day');
  }

  function getMaxTime(dateSelection: Moment, maxDate?: Moment): Moment {
    const nowLocal = userTimezone ? moment().tz(userTimezone) : moment().local();
    if (!maxDate) {
      return nowLocal.endOf('day');
    }
    if (dateSelection && maxDate.isAfter(dateSelection.clone().endOf('day'))) {
      return nowLocal.endOf('day');
    }
    if (dateSelection && maxDate.isAfter(dateSelection.clone().startOf('day'))) {
      return maxDate;
    }
    return nowLocal.endOf('day');
  }

  function shouldDisableEnrollmentCutoff(): boolean {
    return startMomentLocalized.clone().add(2, 'days').isSameOrAfter(endMomentLocalized);
  }

  useEffect(() => {
    if (shouldDisableEnrollmentCutoff()) {
      onClearEnrollmentEnd();
    }
  }, [dateStart, dateEnd]);

  function getScheduleCardHelpText(): string {
    if (campaign.isMailrailDrip) {
      return t(
        'People who satisfy the enrollment triggers during the specified timeframe will be enrolled in the drip series.',
      );
    }
    if (campaign.isMailrailAutoSend) {
      return t(
        'People who meet the triggers of this campaign during the specified timeframe will receive your message.',
      );
    }
    return t("Choose whether you'd like to send your email now or at a later date.");
  }

  function getStartDateHelpText(): string {
    if (isTimeSubtype) {
      if (campaign.isMailrailDrip) {
        return t('The date on which Feathr should start enrolling contacts in the drip series.');
      }
      return t(
        'The date on which Feathr should start sending emails to contacts who match the trigger.',
      );
    }
    const formattedStartTime = startMoment.format(TimeFormat.pickerDateTimeZone);
    if (campaign.isMailrailDrip) {
      return t(
        'The time when Feathr should start enrolling contacts in the drip series. {{date}}',
        { date: formattedStartTime },
      );
    }
    if (campaign.isMailrailAutoSend) {
      return t(
        'The time when Feathr should start sending emails to contacts who perform the trigger. {{date}}',
        { date: formattedStartTime },
      );
    }
    return t('The date and time your email will be sent. {{date}}', { date: formattedStartTime });
  }

  function getEndDateHelpText(): string {
    if (isTimeSubtype) {
      if (campaign.isMailrailDrip) {
        return t('The last date on which Feathr should send emails to enrolled contacts.');
      }
      return t(
        'The last date on which Feathr should send emails to contacts who match the trigger.',
      );
    }
    const formattedEndTime = endMoment?.format(TimeFormat.pickerDateTimeZone) || '';
    if (campaign.isMailrailDrip) {
      return t(
        'The time when Feathr should end enrollment and stop sending emails to enrolled contacts. {{date}}',
        { date: formattedEndTime },
      );
    }
    return t(
      'The time when Feathr should stop sending emails to contacts who perform the trigger. {{date}}',
      { date: formattedEndTime },
    );
  }

  function getEnrollmentEndDateHelpText(): string {
    if (shouldDisableEnrollmentCutoff()) {
      return t(
        'Optional: Choose the last date on which enrollment will occur. Previously enrolled recipients will continue progressing through the steps until the campaign’s end date.',
      );
    }
    if (isTimeSubtype) {
      return t(
        'Optional: Choose the last date on which enrollment will occur. Previously enrolled recipients will continue progressing through the steps until the campaign’s end date.',
      );
    }
    return t(
      'Optional: Choose a date/time at which enrollment in the drip will end. Previously enrolled recipients will continue progressing through the steps until the campaign’s end date. {{date}}',
      { date: enrollmentEndMoment?.format(TimeFormat.pickerDateTimeZone) || '' },
    );
  }

  const validationErrors = validateStepScheduleSend(campaign);

  return (
    <Form label={t('Edit Campaign: Schedule & Publish')} width={'wide'}>
      {campaign.isMailrailPartnerMessage ? (
        <MailrailPartnerAudienceSection
          campaign={campaign as MailrailPartnerMessageModel}
          disabled={disabled}
        />
      ) : (
        <>
          <MailrailCampaignSegments campaign={campaign} disabled={disabled} step={stepNumber} />
          <MailrailCampaignSendingOptions
            campaign={campaign}
            disabled={disabled}
            validationErrors={validationErrors}
          />
        </>
      )}
      <CardV2 width={'full'}>
        <CardHeader description={getScheduleCardHelpText()} title={t('Schedule & Send')} />
        <CardContent>
          <Fieldset className={styles.schedule}>
            {campaign.isDraft && !isTimeSubtype && (
              <Radios
                dataName={'send_schedule'}
                disabled={disabled}
                label={isTriggerable ? t('Choose start time') : t('Choose send time')}
                onChange={onSendtimeChange}
                options={[
                  { id: 'now', name: t('Now') },
                  { id: 'later', name: t('Later') },
                ]}
                value={sendSchedule}
              />
            )}
            {isTimeSubtype && (
              <DatePicker
                autoComplete={'off'}
                dateFormat={'h:mm aa'}
                disabled={disabled}
                helpText={t(
                  'Choose the time of day when emails should be sent to contacts matching the date trigger(s).',
                )}
                label={t('Time of day')}
                name={'time_of_day'}
                onDateStrChange={handleChangeTimeOfDay}
                selected={endMomentLocalized?.toDate()}
                showTimeSelect={true}
                showTimeSelectOnly={true}
                suffix={moment.tz(userTimezone || moment.tz.guess()).zoneAbbr()}
                timeIntervals={5}
                validationError={flattenError(validationErrors.date_start)}
                wrapperClassName={styles.timeOfDaySelect}
              />
            )}
            {sendSchedule === 'later' && (
              <DatePicker
                autoComplete={'off'}
                dateFormat={isTimeSubtype ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm aa'}
                disabled={disabled || campaign.hasStarted}
                helpPlacement={'top'}
                helpText={getStartDateHelpText()}
                label={isTriggerable ? t('Start date') : t('Send date')}
                maxTime={moment().local().endOf('day').toDate()}
                minDate={getMinStartDate().toDate()}
                minTime={getMinTime(startMomentLocalized, getMinStartDate()).toDate()}
                name={'date_start'}
                onDateStrChange={onStartDateChange}
                selected={startMomentLocalized.toDate()}
                showTimeSelect={!isTimeSubtype}
                suffix={moment.tz(userTimezone || moment.tz.guess()).zoneAbbr()}
                timeIntervals={5}
                validationError={flattenError(validationErrors.date_start)}
              />
            )}
            {isTriggerable && (
              <DatePicker
                autoComplete={'off'}
                dateFormat={isTimeSubtype ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm aa'}
                disabled={disabled}
                helpPlacement={'top'}
                helpText={getEndDateHelpText()}
                label={t('End date')}
                maxTime={moment().local().endOf('day').toDate()}
                minDate={getMinEndDate().toDate()}
                minTime={getMinTime(
                  endMomentLocalized || getMinEndDate(),
                  getMinEndDate(),
                ).toDate()}
                name={'date_end'}
                onDateStrChange={onEndDateChange}
                openToDate={endMomentLocalized ? undefined : getMinEndDate().toDate()}
                selected={endMomentLocalized?.toDate()}
                showTimeSelect={!isTimeSubtype}
                suffix={moment.tz(userTimezone || moment.tz.guess()).zoneAbbr()}
                timeIntervals={5}
                validationError={flattenError(validationErrors.date_end)}
              />
            )}
            {campaign.isMailrailDrip && (
              <DatePicker
                autoComplete={'off'}
                dateFormat={isTimeSubtype ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm aa'}
                disabled={disabled || shouldDisableEnrollmentCutoff()}
                helpPlacement={'top'}
                helpText={getEnrollmentEndDateHelpText()}
                isClearable={true}
                label={t('Enrollment cutoff')}
                maxDate={getMaxEnrollmentCutoff()?.toDate()}
                maxTime={getMaxTime(
                  enrollmentEndMomentLocalized || getMinEndDate(),
                  getMaxEnrollmentCutoff(),
                ).toDate()}
                minDate={getMinEndDate().toDate()}
                minTime={getMinTime(
                  enrollmentEndMomentLocalized || getMinEndDate(),
                  getMinEndDate(),
                ).toDate()}
                name={'date_enrollment_end'}
                onClear={onClearEnrollmentEnd}
                onDateStrChange={onEnrollmentEndChange}
                openToDate={enrollmentEndMomentLocalized ? undefined : getMinEndDate().toDate()}
                selected={enrollmentEndMomentLocalized?.toDate()}
                showTimeSelect={!isTimeSubtype}
                suffix={moment.tz(userTimezone || moment.tz.guess()).zoneAbbr()}
                timeIntervals={5}
                validationError={flattenError(validationErrors.date_enrollment_end)}
              />
            )}
          </Fieldset>
        </CardContent>
      </CardV2>
      <CardV2 width={'full'}>
        <CardHeader title={t('Verify & Publish')} />
        <CardContent>
          <ConsentCapture
            alertDescription={t(
              'I understand that if my campaigns exhibit abnormally high bounce or complaint rates, it may result in my account being placed under temporary review or suspension.',
            )}
            alertTitle={t(
              'I attest that I am sending this email to recipients who have signed up and specifically requested to receive email about this topic from me or my organization.',
            )}
            disabled={disabled}
            label={t(
              'Please type "AGREE" in the box below to verify the above statement (case-sensitive).',
            )}
            onChange={handleChangeConsent}
            value={campaign.get('consent', { has_consent: false }).has_consent}
          />
        </CardContent>
      </CardV2>
      {!campaign.isMailrailPartnerMessage &&
        !!stats?.num_targeted &&
        [CampaignState.Published, CampaignState.Stopped].includes(campaign.get('state')) &&
        campaign.isAfterDateStart && <PinpointEmailCampaignAudienceSummary stats={stats} />}
    </Form>
  );
}

export default observer(MailrailCampaignStepScheduleSend);
